import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import PublicNavbar from "@/components/PublicNavbar";
import PublicFooter from "@/components/PublicFooter";
import WhatsAppButton from "@/components/WhatsAppButton";
import { api } from "@/lib/api";
import { GraduationCap, Heart, UtensilsCrossed, Users, Sprout, ArrowRight, Target } from "lucide-react";

const PROGRAMS = [
  {
    id: "child-education",
    icon: GraduationCap,
    color: "from-blue-600 to-indigo-700",
    title: "Child Education",
    img: "/pmr-photos/pmr-1.jpg",
    desc: "We provide quality education to underprivileged children through our learning centers, school supplies, scholarships, and after-school tutoring programs. Our goal is to ensure every child has access to education regardless of their economic background.",
    stats: [
      { v: "2,500+", l: "Students Supported" },
      { v: "15", l: "Schools" },
      { v: "200+", l: "Scholarships" },
    ],
  },
  {
    id: "healthcare",
    icon: Heart,
    color: "from-rose-500 to-red-600",
    title: "Healthcare Aid",
    img: "/pmr-photos/pmr-5.jpg",
    desc: "Our healthcare initiatives bring medical services to remote villages through mobile health camps, free medical checkups, medicine distribution, and health awareness programs. We focus on preventive care and maternal-child health.",
    stats: [
      { v: "50+", l: "Health Camps" },
      { v: "10,000+", l: "Patients Treated" },
      { v: "100+", l: "Villages Covered" },
    ],
  },
  {
    id: "food",
    icon: UtensilsCrossed,
    color: "from-amber-500 to-orange-600",
    title: "Food Distribution",
    img: "/pmr-photos/pmr-3.jpg",
    desc: "Through our food distribution program, we combat hunger by providing nutritious meals and ration kits to families in need. We run community kitchens and special meal programs during festivals and emergencies.",
    stats: [
      { v: "50,000+", l: "Meals Served" },
      { v: "5,000+", l: "Families Fed" },
      { v: "2,000+", l: "Ration Kits" },
    ],
  },
  {
    id: "women",
    icon: Users,
    color: "from-violet-600 to-fuchsia-600",
    title: "Women Empowerment",
    img: "/pmr-photos/pmr-4.jpg",
    desc: "We empower women through skill development training, self-help groups, microfinance support, and awareness programs on health and legal rights. Our goal is to make women financially independent and confident.",
    stats: [
      { v: "1,000+", l: "Women Trained" },
      { v: "50+", l: "Self-Help Groups" },
      { v: "200+", l: "Workshops" },
    ],
  },
  {
    id: "rural",
    icon: Sprout,
    color: "from-emerald-600 to-teal-700",
    title: "Rural Development",
    img: "/pmr-photos/pmr-2.jpg",
    desc: "Our rural development initiatives focus on building infrastructure, providing clean drinking water, supporting sustainable agriculture, and creating livelihood opportunities in villages across Bihar.",
    stats: [
      { v: "25+", l: "Villages Developed" },
      { v: "100+", l: "Water Points" },
      { v: "500+", l: "Farmers Supported" },
    ],
  },
];

export default function Programs() {
  const [hp, setHp] = useState(null);
  useEffect(() => { api.get("/public/homepage").then((r) => setHp(r.data)).catch(() => {}); }, []);

  return (
    <div className="min-h-screen bg-white">
      <PublicNavbar />

      <section className="bg-gradient-to-b from-blue-50/60 to-white border-b border-slate-200 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-block px-4 py-1.5 rounded-full bg-orange-50 border border-orange-200 text-orange-700 text-xs font-bold">What We Do</div>
          <h1 className="heading mt-5 text-4xl sm:text-5xl lg:text-6xl font-black">Our Programs</h1>
          <p className="mt-5 text-slate-600 max-w-2xl mx-auto">
            Comprehensive programs designed to create lasting impact across education, healthcare, nutrition, and community development.
          </p>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 space-y-16">
        {PROGRAMS.map((p, i) => {
          const I = p.icon;
          const reverse = i % 2 === 1;
          return (
            <div key={p.id} id={p.id} className={`grid lg:grid-cols-2 gap-10 items-center ${reverse ? "lg:[&>*:first-child]:order-last" : ""}`}>
              <div className="relative rounded-3xl overflow-hidden aspect-[5/4] bg-slate-100 shadow-xl">
                <img src={p.img} alt={p.title} className="w-full h-full object-cover" />
                <div className={`absolute top-5 left-5 w-14 h-14 rounded-2xl bg-gradient-to-br ${p.color} text-white flex items-center justify-center shadow-lg`}>
                  <I size={24} />
                </div>
              </div>
              <div>
                <div className="text-xs font-bold uppercase tracking-[0.25em] text-[#F59E0B]">Program {String(i + 1).padStart(2, "0")}</div>
                <h2 className="heading mt-3 text-3xl sm:text-4xl font-black">{p.title}</h2>
                <p className="mt-4 text-slate-600 leading-relaxed">{p.desc}</p>
                <div className="mt-6 grid grid-cols-3 gap-3">
                  {p.stats.map((s) => (
                    <div key={s.l} className="p-4 rounded-xl border border-slate-200 bg-slate-50">
                      <div className="heading text-2xl font-black text-brand">{s.v}</div>
                      <div className="text-[10px] uppercase tracking-[0.15em] font-bold text-slate-500 mt-1">{s.l}</div>
                    </div>
                  ))}
                </div>
                <Link to="/donate" className="mt-6 inline-flex items-center gap-2 bg-[#F59E0B] hover:bg-[#D97706] text-white font-bold rounded-full px-6 py-3">
                  <Target size={16} /> Support This Program <ArrowRight size={14} />
                </Link>
              </div>
            </div>
          );
        })}
      </section>

      {/* Get Involved */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-20">
        <div className="rounded-3xl bg-gradient-to-br from-[#1E3A8A] to-[#172b66] text-white p-10 sm:p-14 text-center">
          <h2 className="heading text-3xl sm:text-4xl font-black">Want to Get Involved?</h2>
          <p className="mt-4 text-blue-100 max-w-2xl mx-auto">Whether you want to donate, volunteer, or partner with us, there are many ways to contribute to our mission.</p>
          <div className="mt-8 flex gap-3 justify-center flex-wrap">
            <Link to="/donate" className="bg-[#F59E0B] hover:bg-[#D97706] text-white font-bold rounded-full px-8 py-3.5 inline-flex items-center gap-2">
              Donate Now <ArrowRight size={16} />
            </Link>
            <Link to="/register" className="bg-white/10 hover:bg-white/20 border border-white/30 text-white font-bold rounded-full px-8 py-3.5">
              Join as Volunteer
            </Link>
          </div>
        </div>
      </section>

      <PublicFooter contact={hp} />
      <WhatsAppButton number={hp?.whatsapp_number || "919155561892"} />
    </div>
  );
}
